import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { concatWithDot } from 'helpers/string';
import { ObjectValues } from 'helpers/types';

import { CostOfEachUnitForm } from '../CostOfEachUnitForm';
import {
  costFoEachUnitInitialValues,
  ICostOfEachUnitValuesType,
  useGetCostOfEachUnitInitialValues,
} from '../CostOfEachUnitForm/utils/form';
import { CostOfSalesForm } from '../CostOfSalesForm';
import {
  costOfSalesInitialValues,
  ICostOfSalesValuesType,
  useGetCostOfSalesInitialValues,
} from '../CostOfSalesForm/utils/form';
import { GeneralAndAdministrativeForm } from '../GeneralAndAdministrativeForm';
import {
  generalAndAdministrativeInitialValues,
  IGeneralAndAdministrativeValuesType,
  useGetGeneralAndAdministrativeInitialValues,
} from '../GeneralAndAdministrativeForm/utils/form';
import { MonthlyExpensesForm } from '../MonthlyExpensesForm';
import { IMonthlyExpensesFormValuesType, monthlyExpensesFormInitialValues } from '../MonthlyExpensesForm/utils/form';
import { PersonnelExpensesForm } from '../PersonnelExpensesForm';
import { IPersonnelExpensesFormValuesType, personnelExpensesInitialValues } from '../PersonnelExpensesForm/utils/form';
import { RAndDForm } from '../RAndDForm';
import { IRAndDValuesType, rAndDInitialValues, useGetRAndDInitialValues } from '../RAndDForm/utils/form';
import { SalesAndMarketingForm } from '../SalesAndMarketingForm';
import {
  ISalesAndMarketingValuesType,
  salesAndMarketingInitialValues,
  useGetSalesAndMarketingInitialValues,
} from '../SalesAndMarketingForm/utils/form';
import { TotalExpensesForm } from '../TotalExpensesForm';
import { ITotalExpensesFormValuesType, totalExpensesInitialValues } from '../TotalExpensesForm/utils/form';

export const plannedExpensesPrefix = concatWithDot(
  BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN,
  BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PLANNED_EXPENSES,
);

export const EXPENSE_TYPE_NAMES = {
  MONTHLY_EXPENSES: 'monthlyExpenses',
  COST_OF_EACH_UNIT: 'costOfEachUnit',
  TOTAL_EXPENSES: 'totalExpenses',
  COST_OF_SALES: 'costOfSales',
  R_AND_D: 'r&D',
  SALES_AND_MARKETING: 'salesAndMarketing',
  GENERAL_AND_ADMINISTRATIVE: 'generalAndAdministrative',
  PERSONNEL_EXPENSES: 'personnelExpenses',
} as const;

export type IPlannedExpenseValuesType = ObjectValues<typeof EXPENSE_TYPE_NAMES>;

export const expenseToComponentMap: Record<IPlannedExpenseValuesType, FC> = {
  [EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES]: MonthlyExpensesForm,
  [EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT]: CostOfEachUnitForm,
  [EXPENSE_TYPE_NAMES.TOTAL_EXPENSES]: TotalExpensesForm,
  [EXPENSE_TYPE_NAMES.COST_OF_SALES]: CostOfSalesForm,
  [EXPENSE_TYPE_NAMES.R_AND_D]: RAndDForm,
  [EXPENSE_TYPE_NAMES.SALES_AND_MARKETING]: SalesAndMarketingForm,
  [EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE]: GeneralAndAdministrativeForm,
  [EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES]: PersonnelExpensesForm,
};

export const useGetPlannedExpenseOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const PLANNED_EXPENSE_OPTIONS = [
      {
        label: t('financialPlan.plannedExpenses.expenseType.monthlyExpenses'),
        value: EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.costOfEachUnit'),
        value: EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.totalExpenses'),
        value: EXPENSE_TYPE_NAMES.TOTAL_EXPENSES,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.costOfSales'),
        value: EXPENSE_TYPE_NAMES.COST_OF_SALES,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.r&D'),
        value: EXPENSE_TYPE_NAMES.R_AND_D,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.salesAndMarketing'),
        value: EXPENSE_TYPE_NAMES.SALES_AND_MARKETING,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.generalAndAdministrative'),
        value: EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE,
      },
      {
        label: t('financialPlan.plannedExpenses.expenseType.personnelExpenses'),
        value: EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES,
      },
    ];
    return PLANNED_EXPENSE_OPTIONS;
  }, [t, i18n]);
};

export const monthlyExpensesPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES);
export const costOfEachUnitPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT);
export const totalExpensesPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.TOTAL_EXPENSES);
export const costOfSalesPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.COST_OF_SALES);
export const rAndDPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.R_AND_D);
export const salesAndMarketingPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.SALES_AND_MARKETING);
export const generalAndAdministrativePrefix = concatWithDot(
  plannedExpensesPrefix,
  EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE,
);
export const personnelExpensesPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES);

export interface IPlannedExpenses {
  [EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES]: IMonthlyExpensesFormValuesType;
  [EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT]: ICostOfEachUnitValuesType;
  [EXPENSE_TYPE_NAMES.TOTAL_EXPENSES]: ITotalExpensesFormValuesType;
  [EXPENSE_TYPE_NAMES.COST_OF_SALES]: ICostOfSalesValuesType;
  [EXPENSE_TYPE_NAMES.R_AND_D]: IRAndDValuesType;
  [EXPENSE_TYPE_NAMES.SALES_AND_MARKETING]: ISalesAndMarketingValuesType;
  [EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE]: IGeneralAndAdministrativeValuesType;
  [EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES]: IPersonnelExpensesFormValuesType;
}

export const plannedExpensesInitialValues = {
  [EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES]: monthlyExpensesFormInitialValues,
  [EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT]: costFoEachUnitInitialValues,
  [EXPENSE_TYPE_NAMES.TOTAL_EXPENSES]: totalExpensesInitialValues,
  [EXPENSE_TYPE_NAMES.COST_OF_SALES]: costOfSalesInitialValues,
  [EXPENSE_TYPE_NAMES.R_AND_D]: rAndDInitialValues,
  [EXPENSE_TYPE_NAMES.SALES_AND_MARKETING]: salesAndMarketingInitialValues,
  [EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE]: generalAndAdministrativeInitialValues,
  [EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES]: personnelExpensesInitialValues,
};

export const useGetPlannedExpensesInitialValues = (): IPlannedExpenses => {
  const costFoEachUnitInitialValues = useGetCostOfEachUnitInitialValues();
  const costOfSalesInitialValues = useGetCostOfSalesInitialValues();
  const generalAndAdministrativeInitialValues = useGetGeneralAndAdministrativeInitialValues();
  const rAndDInitialValues = useGetRAndDInitialValues();
  const salesAndMarketingInitialValues = useGetSalesAndMarketingInitialValues();

  return useMemo(
    () => ({
      [EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES]: monthlyExpensesFormInitialValues,
      [EXPENSE_TYPE_NAMES.COST_OF_EACH_UNIT]: costFoEachUnitInitialValues,
      [EXPENSE_TYPE_NAMES.TOTAL_EXPENSES]: totalExpensesInitialValues,
      [EXPENSE_TYPE_NAMES.COST_OF_SALES]: costOfSalesInitialValues,
      [EXPENSE_TYPE_NAMES.R_AND_D]: rAndDInitialValues,
      [EXPENSE_TYPE_NAMES.SALES_AND_MARKETING]: salesAndMarketingInitialValues,
      [EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE]: generalAndAdministrativeInitialValues,
      [EXPENSE_TYPE_NAMES.PERSONNEL_EXPENSES]: personnelExpensesInitialValues,
    }),
    [
      costFoEachUnitInitialValues,
      costOfSalesInitialValues,
      generalAndAdministrativeInitialValues,
      rAndDInitialValues,
      salesAndMarketingInitialValues,
    ],
  );
};

export const EXPENSE_COLUMN_NAMES = {
  MONTHLY_EXPENSES: 'monthlyExpenses',
  ANNUAL_GROWTH: 'annualGrowth',
  COLUMN_0: 'column0',
  COLUMN_1: 'column1',
  ROW_ITEM_NAME: 'rowItemName',
} as const;

export type ExpenseTable = {
  [EXPENSE_COLUMN_NAMES.COLUMN_0]: string;
  [EXPENSE_COLUMN_NAMES.COLUMN_1]: string;
  [EXPENSE_COLUMN_NAMES.ROW_ITEM_NAME]: string;
};
